import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { api, subscribeJob } from '../api/client.js';

function prettyStep(step) {
  if (!step) return '';
  if (step === 'queued') return 'En cola';
  if (step === 'starting') return 'Empezando';
  if (step === 'character_sheet') return 'Dibujando al protagonista';
  if (step === 'character_sheet:done') return 'Protagonista listo';
  if (step === 'pdf') return 'Maquetando el PDF';
  if (step === 'audio:cover') return 'Grabando la portada con voz';
  if (step === 'audio:cover:done') return 'Portada con voz lista';
  if (step === 'ready') return 'Listo';
  if (step === 'done') return 'Listo';
  const sceneMatch = step.match(/^scene:(\d+)(:done)?$/);
  if (sceneMatch) {
    const n = sceneMatch[1];
    return sceneMatch[2] ? `Escena ${n} lista` : `Pintando escena ${n}`;
  }
  const audioMatch = step.match(/^audio:scene:(\d+)(:done)?$/);
  if (audioMatch) {
    const n = audioMatch[1];
    return audioMatch[2] ? `Voz escena ${n} lista` : `Grabando voz escena ${n}`;
  }
  return step;
}

export default function GeneratingPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe = () => {};

    api.jobs
      .get(jobId)
      .then((j) => {
        if (cancelled) return;
        setJob(j);
        if (j.status === 'done') goToReader();
        else if (j.status === 'failed') setError(j.error ?? 'Generacion fallida');
        else {
          unsubscribe = subscribeJob(jobId, ({ event, data }) => {
            if (event === 'snapshot' || event === 'progress') {
              setJob((prev) => ({ ...prev, ...data }));
            } else if (event === 'done') {
              setJob((prev) => ({ ...prev, status: 'done', progress: 100 }));
              setTimeout(goToReader, 600);
            } else if (event === 'failed') {
              setError(data?.error ?? 'Generacion fallida');
            }
          });
        }
      })
      .catch((e) => setError(e.message));

    async function goToReader() {
      const slug = location.state?.slug;
      if (slug) {
        navigate(`/story/${slug}`);
      } else {
        // Fallback: refrescamos el snapshot para obtener el storyId, luego buscamos por listado
        try {
          const j = await api.jobs.get(jobId);
          // Sin slug en mano, vamos a Home
          navigate('/');
        } catch {
          navigate('/');
        }
      }
    }

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [jobId]);

  const progress = job?.progress ?? 0;
  const step = job?.currentStep;

  return (
    <div className="mx-auto max-w-2xl px-4 py-16">
      <h1 className="text-3xl font-bold text-brand-700">Creando tu cuento...</h1>
      <p className="mt-1 text-gray-600">
        Esto tarda alrededor de un minuto. Estamos pintando cada escena con el mismo personaje.
      </p>

      <div className="mt-8 rounded-2xl border border-brand-100 bg-white p-6 shadow-sm">
        <div className="mb-2 flex items-center justify-between text-sm">
          <span className="font-semibold text-brand-700">{prettyStep(step) || 'Preparando'}</span>
          <span className="text-gray-500">{progress}%</span>
        </div>
        <div className="h-3 w-full overflow-hidden rounded-full bg-brand-50">
          <div
            className="h-full rounded-full bg-gradient-to-r from-brand-500 to-brand-700 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        {error && (
          <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {!error && (
          <p className="mt-6 text-center text-sm text-gray-500">
            Cuando termine, te llevamos automaticamente al cuento.
          </p>
        )}
      </div>
    </div>
  );
}
